/**
 * O sino da barra superior, com a contagem do que está vencendo.
 *
 * Papel: mostra um `IconBell` com um selo com o número de itens de
 * `useItensVencendo`, e chama `onAbrir` ao ser clicado. O selo fica vermelho
 * quando há algum item vencido e âmbar quando só há itens dentro da janela.
 *
 * Como usar: quem monta é o `Layout`, que também guarda o estado de abertura e
 * o repassa à `PopupVencimentosDaAbertura` — é essa caixa, e não outra, que o
 * sino abre. Com a lista vazia o botão fica desabilitado, porque abrir a caixa
 * sem item nenhum não mostraria nada, e o rótulo acessível diz isso.
 *
 * A contagem vem do mesmo `useItensVencendo` que a caixa usa, e o TanStack
 * Query guarda a consulta pela chave: o sino e a caixa não disparam requisição
 * a mais por estarem montados juntos.
 */
import { IconBell } from '@tabler/icons-react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { useItensVencendo } from './use-itens-vencendo';

interface BotaoDeVencimentosProps {
  /** Chamado pelo clique; quem monta abre a caixa de vencimentos. */
  onAbrir: () => void;
}

/**
 * O botão de sino da barra superior.
 *
 * @param onAbrir - Chamado quando há itens e o botão é clicado.
 * @returns O botão, com o selo da contagem quando há algum item.
 */
export function BotaoDeVencimentos({ onAbrir }: BotaoDeVencimentosProps) {
  const { itens } = useItensVencendo();
  const quantidade = itens.length;
  const temVencido = itens.some((item) => item.vencido);

  const rotulo =
    quantidade === 0
      ? 'Nenhum item vencendo'
      : `${quantidade} ${quantidade === 1 ? 'item vencendo' : 'itens vencendo'}`;

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className="relative"
      aria-label={rotulo}
      title={rotulo}
      disabled={quantidade === 0}
      onClick={onAbrir}
    >
      <IconBell aria-hidden="true" />
      {quantidade > 0 && (
        <span
          aria-hidden="true"
          className={cn(
            'absolute -top-0.5 -right-0.5 min-w-4 rounded-full px-1 font-mono text-[10px] leading-4 text-primary-foreground',
            temVencido ? 'bg-perigo' : 'bg-aviso',
          )}
        >
          {quantidade}
        </span>
      )}
    </Button>
  );
}
